import { Fragment, useState } from 'react'
import { ChevronDown, ChevronRight, Pencil, Plus } from 'lucide-react'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtDate } from '@/shared/lib/format'
import type { SurveyGroupDto } from '@/shared/api/types'
import { getGroup, listGroups } from './api'
import { TrackerDialog } from './TrackerDialog'
import { TrendPanel } from './TrendPanel'
import { WaveDialog } from './WaveDialog'
import { count } from './vocab'

/**
 * Trackers — a series of waves asking the same questions over time.
 *
 * ## Why this screen had to exist before any of it worked
 *
 * `SurveyRequest` carries `groupId` and `waveId`, and `SurveyResponse` reads them
 * back as `groupName`/`waveNo`. Nothing in the application could create either
 * a group or a wave, so both fields could only ever be null and the tracker
 * column on the poll drawer could only ever be a dash. All five
 * `/api/v1/survey-groups` routes were unreachable. This screen is the reachable
 * half.
 *
 * ## Why waves load per tracker instead of with the list
 *
 * `GET /survey-groups` returns `waves` as an EMPTY LIST on every row — the server
 * makes a deliberate list/detail split (`toResponse(group, withWaves=false)`), so
 * a wave count on the table would have to come from N detail reads on page load.
 * One tracker is open at a time and its detail is fetched then. The wave column
 * therefore reads as a dash until a tracker is opened, which is the same "no
 * figure" mark used everywhere else in this feature — not a zero, because zero
 * waves and an unfetched count are different statements.
 *
 * ## What the wave panel now draws
 *
 * Until `GET /survey-groups/{id}/trend` existed, an open tracker ended in a
 * paragraph explaining that no endpoint lined two waves up and that a chart here
 * would be inventing the comparison it appeared to report. That read now exists,
 * so the paragraph is gone and {@link TrendPanel} is in its place — reading
 * estimates the server already published, and rendering the server's own sentence
 * wherever it declines to publish a difference.
 */
export function TrackersPage() {
  const canRead = useCan('survey:read')
  const canCreate = useCan('survey:create')
  const canUpdate = useCan('survey:update')

  const [openId, setOpenId] = useState<string | null>(null)
  const [editing, setEditing] = useState<{ group: SurveyGroupDto | null } | null>(null)
  const [addingWaveTo, setAddingWaveTo] = useState<SurveyGroupDto | null>(null)

  const groups = useLoad(() => listGroups(), [])
  // An accordion, not a set of independent expanders: the detail read is a
  // request per tracker, and a screen that fires one for every row somebody
  // clicks open is the N+1 the list/detail split was made to avoid.
  const detail = useLoad(
    () => (openId ? getGroup(openId) : Promise.resolve(null)), [openId])

  if (!canRead) return <Forbidden what="trackers" />

  const rows = groups.data ?? []

  /** Every write returns the whole group, so the open row and the list both refresh. */
  const afterWrite = (saved: SurveyGroupDto) => {
    setEditing(null)
    setAddingWaveTo(null)
    groups.reload()
    if (openId === saved.id) detail.reload()
    else setOpenId(saved.id)
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Trackers"
        description="A tracker is a group of waves. A poll belongs to one wave, and that tag is the only thing that says two polls are the same question asked twice."
        right={canCreate ? (
          <button type="button" className="erp-btn-primary h-9 text-erp-sm"
            onClick={() => setEditing({ group: null })}>
            <Plus className="h-3.5 w-3.5 mr-1 inline" />
            New tracker
          </button>
        ) : undefined}
      />

      <ErrorStrip message={groups.error} />

      <Note tone="info">
        Declaring a wave here does not tag any poll into it. The tag is the poll's own
        {' '}<code className="font-mono">groupId</code> and <code className="font-mono">waveId</code>,
        the server accepts both on create and update, and no screen in this build writes them
        yet — so the poll counts below stay at zero until one does. Named rather than left to be
        discovered: a wave whose count is always zero reads as a wave nobody fielded.
      </Note>

      {groups.loading && rows.length === 0 ? <LoadingBlock label="Loading trackers…" /> : (
        <Panel title={`${rows.length} tracker${rows.length === 1 ? '' : 's'}`}>
          <ErpTable head={(
            <>
              <Th className="w-8" />
              <Th>Code</Th>
              <Th>Name</Th>
              <Th className="text-right">Polls</Th>
              <Th className="text-right">Waves</Th>
              <Th>Declared</Th>
              <Th className="w-8" />
            </>
          )}>
            {rows.length === 0 ? (
              <EmptyRow colSpan={7} message={
                'No trackers yet. Declare one, then add a wave to it — until a tracker and a '
                + 'wave exist, no poll can say it is the second reading of the same question.'
              } />
            ) : rows.map((group) => {
              const open = openId === group.id
              const loaded = open && detail.data?.id === group.id ? detail.data : null
              return (
                <Fragment key={group.id}>
                  <tr className="hover:bg-muted/40">
                    <Td className="text-muted-foreground">
                      {/*
                        A real button, not a `<tr onClick>`: the waves and "Add
                        wave" live behind this expander and nothing else in the
                        product reaches them, so a row that only answers a mouse
                        puts a whole screen out of reach of a keyboard.
                      */}
                      <button
                        type="button"
                        className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                        aria-expanded={open}
                        aria-label={`${open ? 'Hide' : 'Show'} the waves of ${group.code}`}
                        onClick={() => setOpenId(open ? null : group.id)}
                      >
                        {open ? <ChevronDown className="h-3.5 w-3.5" />
                          : <ChevronRight className="h-3.5 w-3.5" />}
                      </button>
                    </Td>
                    <Td className="font-mono text-erp-xs text-chocolate">{group.code}</Td>
                    <Td>
                      <div>{group.name}</div>
                      {group.description && (
                        <div className="text-erp-xs text-muted-foreground">{group.description}</div>
                      )}
                    </Td>
                    <Td className="text-right tabular-nums">{count(group.pollCount)}</Td>
                    <Td className="text-right tabular-nums text-muted-foreground">
                      {loaded ? count(loaded.waves.length) : '—'}
                    </Td>
                    <Td className="text-muted-foreground">{fmtDate(group.createdAt)}</Td>
                    <Td>
                      {canUpdate && (
                        <button type="button" className="erp-btn-ghost h-7 px-1.5 text-erp-xs"
                          aria-label={`Edit ${group.code}`}
                          onClick={() => setEditing({ group })}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </Td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={7} className="bg-muted/20 border-b border-border p-4">
                        <WavePanel
                          group={loaded}
                          loading={detail.loading}
                          error={detail.error}
                          canUpdate={canUpdate}
                          onAddWave={() => loaded && setAddingWaveTo(loaded)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </ErpTable>
        </Panel>
      )}

      <Note tone="info">
        The wave count is blank until a tracker is opened. The list read returns waves as an
        empty array on every row by design, and printing zero for a count that read never
        carried would be a figure nobody sent.
      </Note>

      {editing && (
        <TrackerDialog
          open
          group={editing.group}
          onClose={() => setEditing(null)}
          onSaved={afterWrite}
        />
      )}

      {addingWaveTo && (
        <WaveDialog
          open
          group={addingWaveTo}
          onClose={() => setAddingWaveTo(null)}
          onSaved={afterWrite}
        />
      )}
    </div>
  )
}

/**
 * One tracker's waves, in order, and the trend across them.
 *
 * The poll count is the only number `WaveResponse` itself carries — there is no
 * estimate and no share on it. Everything below the table comes from the trend
 * read instead, which is scoped to one question because a tracker carries a whole
 * instrument and one line across all of it would be averaging answers to
 * different questions.
 */
function WavePanel({ group, loading, error, canUpdate, onAddWave }: {
  group: SurveyGroupDto | null
  loading: boolean
  error: string | null
  canUpdate: boolean
  onAddWave: () => void
}) {
  if (error) return <ErrorStrip message={error} />
  if (!group) return <LoadingBlock label="Loading waves…" />

  const waves = group.waves.slice().sort((a, b) => a.waveNo - b.waveNo)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h5 className="text-erp-sm font-medium">
          {waves.length === 0 ? 'No waves yet'
            : `${waves.length} wave${waves.length === 1 ? '' : 's'}`}
        </h5>
        {canUpdate && (
          <button type="button" className="erp-btn-ghost h-8 text-erp-sm"
            disabled={loading} onClick={onAddWave}>
            <Plus className="h-3.5 w-3.5 mr-1 inline" />
            Add wave
          </button>
        )}
      </div>

      <ErpTable head={(
        <>
          <Th className="text-right">Wave</Th>
          <Th>Label</Th>
          <Th>Opens</Th>
          <Th>Closes</Th>
          <Th className="text-right">Polls</Th>
        </>
      )}>
        {waves.length === 0 ? (
          <EmptyRow colSpan={5} message={
            'A tracker with no waves tags nothing. Add the first one — the server numbers it.'
          } />
        ) : waves.map((wave) => (
          <tr key={wave.id} className="hover:bg-muted/40">
            <Td className="text-right tabular-nums font-medium">{wave.waveNo}</Td>
            <Td>{wave.label ?? '—'}</Td>
            <Td className="text-muted-foreground">
              {wave.opensAt ? fmtDate(wave.opensAt) : '—'}
            </Td>
            <Td className="text-muted-foreground">
              {wave.closesAt ? fmtDate(wave.closesAt) : '—'}
            </Td>
            <Td className="text-right tabular-nums">{count(wave.pollCount)}</Td>
          </tr>
        ))}
      </ErpTable>

      {/*
        The answer to the question this table invites — "so where is the trend?"
        — sits directly under it rather than at the top of the page, for the same
        reason the paragraph it replaced did: put anywhere less obvious it is
        read after the conclusion it qualifies, not before.
      */}
      <TrendPanel group={group} />
    </div>
  )
}
